import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Search, Folder, Image, X, Plus, Loader, Save } from "lucide-react";
import API_BASE_URL from "../../../services/api";
import Button from "../../../components/ui/Button";
import { canAccess } from "../../../utils/permissions";
import { useAuth } from "../../../context/AuthContext";
import axiosInstance from "../../../utils/axiosInstance";

const Category = () => {
  const [categories, setCategories] = useState([]);
  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    type: "", // ✅ optional (VITAL_ORGAN/PROFILE/CURATED_CHECKUP etc.)
    order: "",
    image: null,
    banner: null,
    isPopular: false,
    removeImage: false,
    removeBanner: false,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // API FETCH
  // =========================
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_BASE_URL}/api/categories`);
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.categories || [];
      setCategories(list);
    } catch (error) {
      console.error("Error fetching categories:", error);
      alert("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // VALIDATION
  // =========================
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Category name is required";
    else if (formData.name.trim().length < 2)
      newErrors.name = "Category name must be at least 2 characters";

    if (formData.type && String(formData.type).trim().length < 2) {
      newErrors.type = "Invalid type";
    }

    if (formData.order !== "" && Number.isNaN(Number(formData.order))) {
      newErrors.order = "Order must be a number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // =========================
  // FILE HANDLERS
  // =========================
  const handleImageFile = (file, { kind }) => {
    try {
      if (!file) return;

      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        alert("Please select a valid image (JPEG, PNG, WebP)");
        return;
      }

      const maxSize = kind === "banner" ? 3 * 1024 * 1024 : 2 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(
          kind === "banner"
            ? "Banner size should be < 3MB"
            : "Image size should be < 2MB",
        );
        return;
      }

      if (kind === "image") {
        setFormData((p) => ({ ...p, image: file, removeImage: false }));
        setErrors((p) => ({ ...p, image: undefined }));
        const reader = new FileReader();
        reader.onload = (e) => setImagePreview(e.target.result);
        reader.readAsDataURL(file);
      }

      if (kind === "banner") {
        setFormData((p) => ({ ...p, banner: file, removeBanner: false }));
        setErrors((p) => ({ ...p, banner: undefined }));
        const reader = new FileReader();
        reader.onload = (e) => setBannerPreview(e.target.result);
        reader.readAsDataURL(file);
      }
    } catch (err) {
      console.error("handleImageFile error:", err);
      alert("Failed to process image");
    }
  };

  const removeImage = () => {
    try {
      setFormData((prev) => ({ ...prev, image: null, removeImage: true }));
      setImagePreview(null);
      const fileInput = document.getElementById("image-upload");
      if (fileInput) fileInput.value = "";
    } catch (e) {
      console.error(e);
    }
  };

  const removeBanner = () => {
    try {
      setFormData((prev) => ({ ...prev, banner: null, removeBanner: true }));
      setBannerPreview(null);
      const fileInput = document.getElementById("banner-upload");
      if (fileInput) fileInput.value = "";
    } catch (e) {
      console.error(e);
    }
  };

  // =========================
  // EDIT / RESET
  // =========================
  const resetForm = () => {
    try {
      setFormData({
        name: "",
        type: "",
        order: "",
        image: null,
        banner: null,
        isPopular: false,
        removeImage: false,
        removeBanner: false,
      });

      setImagePreview(null);
      setBannerPreview(null);
      setEditingCategory(null);
      setErrors({});

      const imgEl = document.getElementById("image-upload");
      const banEl = document.getElementById("banner-upload");
      if (imgEl) imgEl.value = "";
      if (banEl) banEl.value = "";
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (category) => {
    try {
      setEditingCategory(category);

      setFormData({
        name: category.name || "",
        type: category.type || "",
        order: category.order ?? "",
        image: null,
        banner: null,
        isPopular: !!category.isPopular,
        removeImage: false,
        removeBanner: false,
      });

      setImagePreview(category.imgUrl || null);
      setBannerPreview(category.bannerUrl || null);
      setErrors({});

      const imgEl = document.getElementById("image-upload");
      const banEl = document.getElementById("banner-upload");
      if (imgEl) imgEl.value = "";
      if (banEl) banEl.value = "";
    } catch (err) {
      console.error("handleEdit error:", err);
    }
  };

  // =========================
  // DELETE
  // =========================
  const handleDelete = async (id) => {
    try {
      if (
        !window.confirm(
          "Are you sure you want to delete this category? This action cannot be undone.",
        )
      )
        return;

      await axios.delete(`${API_BASE_URL}/api/categories/${id}`);
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);

      const msg = error?.response?.data?.message || "Failed to delete category";
      const usage = error?.response?.data?.usage;

      if (usage?.length) {
        alert(
          `${msg}\n\nUsed in:\n- ${usage
            .map((u) => `${u.label}: ${u.count}`)
            .join("\n- ")}`,
        );
      } else {
        alert(msg);
      }
    }
  };

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const finalType = String(formData.type || "").toUpperCase();

      const submitData = new FormData();
      submitData.append("name", formData.name.trim());
      if (finalType) submitData.append("type", finalType);

      submitData.append("removeImage", String(formData.removeImage));
      submitData.append("removeBanner", String(formData.removeBanner));

      if (
        formData.order !== "" &&
        formData.order !== null &&
        formData.order !== undefined
      ) {
        submitData.append("order", String(formData.order));
      }

      submitData.append("isPopular", String(formData.isPopular));

      if (formData.image) submitData.append("image", formData.image);
      if (formData.banner) submitData.append("banner", formData.banner);

      if (editingCategory) {
        await axios.put(
          `${API_BASE_URL}/api/categories/${editingCategory.id}`,
          submitData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/categories`, submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      resetForm();
      fetchCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Failed to ${editingCategory ? "update" : "create"} category`;
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // =========================
  // FILTER + GROUP (by category.type)
  // =========================
  const filteredCategories = categories.filter((category) => {
    const q = searchTerm.toLowerCase();
    return (
      category.name?.toLowerCase().includes(q) ||
      (category.type || "")?.toLowerCase().includes(q) ||
      String(category.order ?? "").toLowerCase().includes(q) ||
      (category.isPopular ? "popular".includes(q) : false)
    );
  });

  const grouped = filteredCategories.reduce((acc, cat) => {
    const t = String(cat?.type || "NOT_SET").toUpperCase();
    if (!acc[t]) acc[t] = [];
    acc[t].push(cat);
    return acc;
  }, {});

  // =========================
  // UI HELPERS
  // =========================
  const getTypeBadgeColor = (type) => {
    switch (String(type || "").toUpperCase()) {
      case "VITAL_ORGAN":
        return "bg-green-100 text-green-800";
      case "PROFILE":
        return "bg-orange-100 text-orange-800";
      case "CURATED_CHECKUP":
        return "bg-rose-100 text-rose-800";
      case "NOT_SET":
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeDisplayName = (type) => {
    switch (String(type || "").toUpperCase()) {
      case "VITAL_ORGAN":
        return "Vital Organ";
      case "PROFILE":
        return "Health Profile";
      case "CURATED_CHECKUP":
        return "Curated Check Ups";
      case "NOT_SET":
        return "Not Set";
      default:
        return type || "Not Set";
    }
  };

  const typeOrder = (t) => {
    const v = String(t || "").toUpperCase();
    if (v === "VITAL_ORGAN") return 1;
    if (v === "PROFILE") return 2;
    if (v === "CURATED_CHECKUP") return 3;
    if (v === "NOT_SET") return 99;
    return 50;
  };

  return (
    <div className="">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Categories</h1>
        <p className="text-gray-600 text-sm mt-0.5">
          Manage your medical service categories
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Side - Add/Edit Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {editingCategory ? "Edit Category" : "Create Category"}
              </h3>
              {editingCategory && (
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, name: e.target.value }));
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="e.g., Blood Tests, X-Ray"
                  disabled={submitting}
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              {/* ✅ Category Type (optional) */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Category Type{" "}
                  <span className="text-gray-500 font-normal">(Optional)</span>
                </label>

                <select
                  value={formData.type}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, type: e.target.value }));
                    setErrors((prev) => ({ ...prev, type: undefined }));
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.type ? "border-red-300" : "border-gray-300"
                  }`}
                  disabled={submitting}
                >
                   <option value="PATHOLOGY">Pathology</option>
                  <option value="RADIOLOGY">Radiology</option>
                  <option value="VITAL_ORGAN">Vital Organ</option>
                  <option value="PROFILE">Health Profile</option>
                  <option value="CURATED_CHECKUP">Curated Check Ups</option>
                </select>

                {errors.type && (
                  <p className="mt-1 text-xs text-red-600">{errors.type}</p>
                )}
              </div>

              {/* Order */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Order{" "}
                  <span className="text-gray-500 font-normal">(Optional)</span>
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, order: e.target.value }));
                    setErrors((prev) => ({ ...prev, order: undefined }));
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.order ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="1..6 for Popular sorting"
                  disabled={submitting}
                />
                {errors.order && (
                  <p className="mt-1 text-xs text-red-600">{errors.order}</p>
                )}
              </div>

              {/* Popular */}
              <div className="flex items-center gap-2">
                <input
                  id="isPopular"
                  type="checkbox"
                  checked={formData.isPopular}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, isPopular: e.target.checked }))
                  }
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  disabled={submitting}
                />
                <label htmlFor="isPopular" className="text-xs text-gray-700">
                  Show in “Popular Checkups” (Mobile App)
                </label>
              </div>

              {/* Category Image */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Category Image{" "}
                  <span className="text-gray-500 font-normal ml-1">
                    (Optional)
                  </span>
                </label>

                {imagePreview ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-40 object-cover rounded border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        disabled={submitting}
                        className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Click X to remove image
                    </p>
                  </div>
                ) : (
                  <div
                    onClick={() =>
                      !submitting &&
                      document.getElementById("image-upload")?.click()
                    }
                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${
                      submitting
                        ? "border-gray-200 bg-gray-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Image className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-xs text-gray-600 font-medium mb-1">
                        Upload Image
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, WebP up to 2MB
                      </p>
                    </div>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageFile(e.target.files?.[0], { kind: "image" })
                  }
                  className="hidden"
                  id="image-upload"
                  disabled={submitting}
                />
              </div>

              {/* Banner Image */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Banner Image{" "}
                  <span className="text-gray-500 font-normal ml-1">
                    (Optional)
                  </span>
                </label>

                {bannerPreview ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <img
                        src={bannerPreview}
                        alt="Banner Preview"
                        className="w-full h-24 object-cover rounded border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeBanner}
                        disabled={submitting}
                        className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Used for Mobile “Popular Checkups”
                    </p>
                  </div>
                ) : (
                  <div
                    onClick={() =>
                      !submitting &&
                      document.getElementById("banner-upload")?.click()
                    }
                    className={`border-2 border-dashed rounded p-3 text-center cursor-pointer transition-colors ${
                      submitting
                        ? "border-gray-200 bg-gray-50"
                        : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
                    }`}
                  >
                    <p className="text-xs text-gray-600 font-medium">
                      Upload Banner
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Recommended 1200×400 • up to 3MB
                    </p>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageFile(e.target.files?.[0], { kind: "banner" })
                  }
                  className="hidden"
                  id="banner-upload"
                  disabled={submitting}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  disabled={
                    !canAccess(currentUser, "canCreate") ||
                    submitting ||
                    !formData.name.trim()
                  }
                  loading={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-2"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-3 h-3 animate-spin mr-1.5" />
                      {editingCategory ? "Updating..." : "Creating..."}
                    </>
                  ) : editingCategory ? (
                    <>
                      <Save className="w-3 h-3 mr-1.5" />
                      Update Category
                    </>
                  ) : (
                    <>
                      <Plus className="w-3 h-3 mr-1.5" />
                      Create Category
                    </>
                  )}
                </Button>

                {editingCategory && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={resetForm}
                    disabled={submitting}
                    className="px-4 border-gray-300 text-gray-700 hover:bg-gray-50 text-xs"
                  >
                    <X className="w-3 h-3 mr-1.5" />
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Categories List (Grouped by Type) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    All Categories
                  </h3>
                  <p className="text-gray-600 text-xs mt-0.5">
                    Total: {categories.length} • Showing:{" "}
                    {filteredCategories.length}
                  </p>
                </div>

                <div className="w-full sm:w-56">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                      <Search className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="p-3">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                  <p className="text-gray-600 text-xs font-medium">
                    Loading categories...
                  </p>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Folder className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm font-medium mb-1">
                    No categories found
                  </p>
                  <p className="text-gray-400 text-xs">
                    {searchTerm
                      ? "No categories match your search"
                      : "Start by creating your first category"}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.keys(grouped)
                    .sort((a, b) => typeOrder(a) - typeOrder(b))
                    .map((t) => {
                      const cats = grouped[t] || [];
                      return (
                        <div
                          key={t}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">
                                {getTypeDisplayName(t)}
                              </span>
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border text-gray-600">
                                {cats.length} categories
                              </span>
                            </div>
                          </div>

                          <div className="p-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {cats.map((category, idx) => (
                                <div
                                  key={category.id}
                                  className={`p-3 border rounded-lg transition-all duration-200 ${
                                    editingCategory?.id === category.id
                                      ? "border-blue-300 bg-blue-50 ring-1 ring-blue-100"
                                      : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                                  }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0">
                                      {category?.imgUrl ? (
                                        <img
                                          src={category.imgUrl}
                                          alt={category.name}
                                          className="w-14 h-14 rounded border border-gray-200 object-cover"
                                        />
                                      ) : (
                                        <div className="w-14 h-14 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                          <Image className="w-6 h-6 text-gray-400" />
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-1.5">
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-2">
                                            <h3 className="text-xs font-semibold text-gray-900 truncate">
                                              {category?.name}
                                            </h3>
                                            {category?.isPopular && (
                                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                                                Popular
                                              </span>
                                            )}
                                          </div>

                                          <div
                                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getTypeBadgeColor(
                                              category.type || "NOT_SET",
                                            )}`}
                                          >
                                            {getTypeDisplayName(
                                              category?.type || "NOT_SET",
                                            )}
                                          </div>

                                          {category?.bannerUrl && (
                                            <div className="mt-2">
                                              <p className="text-[11px] text-gray-500 mb-1">
                                                Banner:
                                              </p>
                                              <img
                                                src={category.bannerUrl}
                                                alt="Banner"
                                                className="w-full h-16 object-cover rounded border border-gray-200"
                                              />
                                            </div>
                                          )}
                                        </div>

                                        <span className="text-xs text-gray-400 font-medium bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                                          #{idx + 1}
                                        </span>
                                      </div>

                                      <div className="flex items-center justify-between mt-3">
                                        <div className="text-xs text-gray-500 truncate">
                                          ID:{" "}
                                          <span className="font-mono text-xs">
                                            {category.id || "N/A"}
                                          </span>
                                          {"  "}•{"  "}
                                          Order:{" "}
                                          <span className="font-mono">
                                            {category.order ?? "-"}
                                          </span>
                                        </div>

                                        <div className="flex gap-1">
                                          <Button
                                            variant="ghost"
                                            size="xs"
                                            iconName="Edit2"
                                            disabled={!canAccess(currentUser, "canEdit")}
                                            onClick={() => handleEdit(category)}
                                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1"
                                          />
                                          <Button
                                            variant="ghost"
                                            size="xs"
                                            iconName="Trash2"
                                            disabled={!canAccess(currentUser, "canDelete")}
                                            onClick={() => handleDelete(category.id)}
                                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category;